import * as React from 'react';
import * as moment from 'moment';
import { PVASelect, PVASelectOption, LoadingIndicatorMini, LoadingIndicator, DataGrid, PVAPage } from '../../components';
import { QuestionDataService, ReportDataService } from '../../services';
import { Question, QuestionOption } from '../../models';
import { BaseRouteProps } from '../../interfaces';
import { UserStore, FilterStore } from '../../stores';
import { Roles } from '../../stores/Constants';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';

class Response {
    public userID: number;
    public firstName: string;
    public lastName: string;
    public response: string;
    public questionOptionID: number;
    public answered: Date;
}

interface ResponsesViewState {
    questions: Question[];
    surveyId: number;
    questionId: number;
    filterId: number;
    responses: Response[];
    loading: boolean;
    anonymous: boolean;
    open: boolean;
    pdfLoader: boolean;
}

interface ResponsesViewProps extends BaseRouteProps<{ id: number }> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

@inject('UserStore', 'FilterStore') @observer
export class ResponsesView extends React.Component<ResponsesViewProps, ResponsesViewState> {
    private questionDataService: QuestionDataService = new QuestionDataService(this.props.notificationService);
    private reportDataService: ReportDataService = new ReportDataService(this.props.notificationService);

    @computed get isSuperAdmin() {
        return this.props.UserStore!.userRoleIn([Roles.SuperAdmin]);
    }

    constructor(props: ResponsesViewProps) {
        super(props);

        this.state = {
            questions: [],
            surveyId: 0,
            questionId: 0,
            filterId: 1,
            responses: [],
            loading: false,
            anonymous: true,
            open: false,
            pdfLoader: false
        };
    }

    @computed get survey() {
        if (this.props.FilterStore && this.props.FilterStore.survey) {
            return this.props.FilterStore.survey;
        }
        return null;
    }

    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get teamId() {
        if (this.props.FilterStore && this.props.FilterStore.teamId) {
            return this.props.FilterStore.teamId;
        }
        return null;
    }

    private update() {
        if (this.survey && this.survey.id !== this.state.surveyId) {
            this.setState({
                surveyId: this.survey.id,
                loading: true
            });
            // get questions
            this.questionDataService.getAllBySurvey(this.survey).then((questions: Question[]) => {
                this.setState({
                    questions: questions,
                    loading: false
                });
            });
        }
    }

    public componentDidMount() {
        this.update();
    }

    public componentWillUpdate() {
        this.update();
    }

    private questionChange(e: any) {
        var questionId = parseInt(e.target.value, 10);
        this.setState({
            questionId: questionId,
            loading: true
        });

        // get question responses

        if (this.teamId && this.teamId > 0) {
            this.questionDataService.getAllResponsesByTeam(questionId, this.teamId).then((responses: Response[]) => {
                this.setState({
                    responses: responses,
                    loading: false
                });
            }).catch(() => {
                this.props.notificationService.addError('Error retrieving question responses data.');
                this.setState({loading: false});
            });
        } else if (this.organization) {
            var fn = this.questionDataService.getAllResponsesByOrganization;
            if (this.teamId) {
                if (this.teamId === -200) { // Candidates
                    fn = this.questionDataService.getAllResponsesByCandidates;
                } else if (this.teamId === -300) { // Former Employees
                    fn = this.questionDataService.getAllResponsesByFormer;
                }
            }
            fn.bind(this.questionDataService)(questionId, this.organization.id).then((responses: Response[]) => {
                this.setState({
                    responses: responses,
                    loading: false
                });
            }).catch(() => {
                this.props.notificationService.addError('Error retrieving question responses data.');
                this.setState({loading: false});
            });
        }
    }

    private filterChange(e: any) {
        var filterId = parseInt(e.target.value, 10);
        this.setState({
            filterId: filterId
        });
    }

    private filterAndSort() {
        return this.state.questions.filter((q: Question) => {
            if (this.state.filterId === 1) {
                return q.questionType === 'binary';
            }
            if (this.state.filterId === 2) {
                return q.questionType === 'scale';
            }
            if (this.state.filterId === 3) {
                return q.questionType === 'text' || q.questionType === 'freeText';
            }
            return false;
        }).sort((q1: Question, q2: Question) => {
            if (q1.sort > q2.sort) {
                return 1;
            } else if (q1.sort < q2.sort) {
                return -1;
            }
            return 0;
        });
    }

    private exportPDF() {
        if (this.state.questionId > 0) {
            this.setState(
            {
                pdfLoader: true,
                open: false
            });

            if (this.teamId) {
                this.reportDataService.getResponsesPDFByTeam(this.state.questionId, this.teamId, this.state.anonymous).then((reportData: any) => {
                    this.setState(
                        {
                            pdfLoader: false
                        });
                    })
                    .catch(() => {
                        this.setState(
                            {
                                pdfLoader: false
                            });
                    });
            } else if (this.organization) {

                var fn = this.reportDataService.getResponsesPDFByOrganization;
                if (this.teamId) {
                    if (this.teamId === -200) { // Candidates
                        fn = this.reportDataService.getResponsesPDFByCandidates;
                    } else if (this.teamId === -300) { // Former Employees
                        fn = this.reportDataService.getResponsesPDFByFormer;
                    }
                }

                fn.bind(this.reportDataService)(this.state.questionId, this.organization.id, this.state.anonymous).then((responses: Response[]) => {
                    this.setState({
                        responses: responses,
                        loading: false
                    });
                }).catch(() => {
                    this.props.notificationService.addError('Error retrieving question responses data.');
                    this.setState({loading: false});
                });
            }
        }
    }
    
    private ActionsButton() {
        return (
            <div className="btn-group dropdown">
                <button type="button" className="btn btn-primary"  onClick={(e) => this.setState({open: !this.state.open })}>
                    <span>Actions </span>
                    <span className="caret" />
                </button>
                <ul className={`action-menu dropdown-menu ${this.state.open ? 'open' : ''}`}>
                    <li className="options-entry" onClick={(e) => this.setState({anonymous: !this.state.anonymous, open: false})}>
                        <div className="options-entry-title">
                            <img src="/images/Icon awesome-search.svg" /> 
                            {this.state.anonymous && <span> Show Names</span>}
                            {!this.state.anonymous && <span> Hide Names</span>}
                        </div>
                        <div className="options-entry-desc">
                            Show/Hide names
                        </div>
                    </li>
                    <li className="options-header">Export Options</li>
                    <li className="options-entry" onClick={(e) => this.exportPDF()}>
                        <div className="options-entry-title">
                            <img src="/images/Icon awesome-file-pdf.svg" /> <span> PDF Export</span>
                        </div>
                    </li>
                </ul>
        </div>);
    }

    private buildResponseGridComponent() {
        const dateAnsweredColumn = ({ value }: any) => {
            return moment(value).format('M/D/YYYY');
        };

        const dateSort = (data: any[], column: any, sortAscending: boolean) => {
            return data.sort(
                (a: any, b: any) => {
                    const aMoment = moment(a.get(column));
                    const bMoment = moment(b.get(column));
        
                    if (aMoment.isSame(bMoment)) {
                        return 0;
                    } else if (aMoment.isBefore(bMoment)) {
                        return sortAscending ? -1 : 1;
                    } else {
                        return sortAscending ? 1 : -1;
                    }
                }
            );            
        };

        let listData: any[], columnDefs: any[];

        if (this.state.anonymous) {
            listData = this.state.responses.map((response: Response, ind: number) => {
                return {
                    response: response.response,
                    dateAnswered: response.answered
                };
            });

            columnDefs = [
                { id: 'response', title: 'Response' },
                { id: 'dateAnswered', title: 'Date Answered', customComponent: dateAnsweredColumn, sortMethod: dateSort}
            ]; 
        } else {
            listData = this.state.responses.map((response: Response, ind: number) => {
                return {
                    firstName: response.firstName,
                    lastName: response.lastName,
                    response: response.response,
                    dateAnswered: response.answered
                };
            });

            columnDefs = [
                { id: 'firstName', title: 'First Name' },
                { id: 'lastName', title: 'Last Name' },
                { id: 'response', title: 'Response' },
                { id: 'dateAnswered', title: 'Date Answered', customComponent: dateAnsweredColumn, sortMethod: dateSort }
            ];
        }

        return <DataGrid key={`datagrid-${moment().format('MM-DD-YYYY-HH-mm-ss-SSS')}`} entities={listData} columns={columnDefs} />;
    }

    public render() {

        var questionOptions: PVASelectOption[] = [];

        this.filterAndSort().map((q: Question, ind: number) => {
            questionOptions.push({
                id: q.id,
                label: '#' + (ind + 1) + ' ' + q.questionTexts[0].value
            });
        });

        var filterOptions: PVASelectOption[] = [];
        if (this.state.questions.filter((q: Question) => {
            return q.questionType === 'binary';
        }).length > 0) {
            filterOptions.push({
                id: 1,
                label: 'True/False'
            });
        }
        if (this.state.questions.filter((q: Question) => {
            return q.questionType === 'scale';
        }).length > 0) {
            filterOptions.push({
                id: 2,
                label: 'Likert'
            });
        }
        if (this.state.questions.filter((q: Question) => {
            return q.questionType === 'text' || q.questionType === 'freeText';
        }).length > 0) {
            filterOptions.push({
                id: 3,
                label: 'Text'
            });
        }

        var question: Question | undefined;
        question = this.state.questions.find((q: Question) => {
            return q.id === this.state.questionId;
        });

        const responseGridComponent = this.buildResponseGridComponent();

        return (
            <PVAPage>
                {this.survey && <div id={`survey-${this.survey.id}`} />}
                <PVAPage.Header>
                    <PVAPage.Header.Title>
                        <PVAPage.Header.Title.Text>
                            Survey Responses
                            <PVASelect options={filterOptions} selected={this.state.filterId} onChange={(e: any) => { this.filterChange(e); }} />
                            <PVASelect options={questionOptions} selected={this.state.questionId} onChange={(e: any) => { this.questionChange(e); }} />
                            {this.state.loading && <LoadingIndicatorMini />}
                        </PVAPage.Header.Title.Text>
                    </PVAPage.Header.Title>
                    <PVAPage.Header.Buttons>
                        {this.ActionsButton()}
                    </PVAPage.Header.Buttons>
                </PVAPage.Header>
                <PVAPage.Body>

                    {this.state.pdfLoader && <LoadingIndicator showPdfDescription={true} showInPanel={true} showBackground={true}/>}

                    {question && question.questionType !== 'text' && question.questionType !== 'freeText' && <div>
                        <h3>Breakdown</h3>
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <td>Option</td>
                                    <td>Count</td>
                                </tr>
                            </thead>
                            <tbody>
                            {question && question.questionOptions && question.questionOptions.map((qo: QuestionOption, ind: number) => {
                                    return <tr key={`qo-${ind}`}>
                                        <td>{qo.questionOptionTexts && qo.questionOptionTexts.length > 0 && qo.questionOptionTexts[0].value}</td>
                                        <td>{this.state.responses.filter((r: Response) => {
                                            return r.questionOptionID === qo.id;
                                        }).length}</td>
                                    </tr>;
                                })}
                            </tbody>
                        </table>
                    </div>}

                    <h3>Responses</h3>
                    {responseGridComponent}

                </PVAPage.Body>
                <PVAPage.Footer />
            </PVAPage>);
    }
}



// WEBPACK FOOTER //
// ./src/components/responses/ResponsesView.tsx